<template>
  <peekai-nudge-banner
    :step="step"
    :progress-step="progressStep"
    :disabled="busy"
    @confirm="onConfirm"
    @view-report="onViewReport"
  />
</template>

<script>
import { create } from 'vue-modal-dialogs'
import AppDialog from '@/components/ui/AppDialog'
import PeekaiNudgeBanner from '@/components/heatmap/PeekaiNudgeBanner'
import { createPeekaiReport, getPeekaiReportAvailability, getPeekaiReportStatus } from '@/api/peekai'

const openDialog = create(AppDialog)

export default {
  name: 'PeekaiNudge',
  components: { PeekaiNudgeBanner },
  props: {
    userNum: { type: [String, Number], required: true },
    sid: { type: String, required: true },
    domain: { type: String, required: true }
  },
  data () {
    return {
      step: 1,
      progressStep: 1,
      reportId: null,
      report: null,
      busy: true,
      availabilityChecked: false,
      pollingTimer: null,
      disposed: false
    }
  },
  created () {
    this.restoreReport()
  },
  beforeDestroy () {
    this.disposed = true
    this.stopPolling()
  },
  methods: {
    async restoreReport () {
      this.busy = true
      try {
        const result = await getPeekaiReportAvailability()
        if (this.disposed) return
        if (!result || typeof result.exists !== 'boolean') throw new Error('Invalid report availability')

        this.availabilityChecked = true
        this.report = null
        this.reportId = result.exists ? result.reportId : null
        if (result.exists) {
          if (!this.reportId) throw new Error('Missing report ID')
          await this.loadStatus()
        } else {
          this.step = 1
          this.progressStep = 1
        }
      } catch (error) {
        this.availabilityChecked = false
        this.showError()
      } finally {
        this.busy = false
      }
    },
    async loadStatus () {
      const reportId = this.reportId
      const result = await getPeekaiReportStatus(reportId)
      if (this.disposed || this.reportId !== reportId) return
      if (!result || result.id !== reportId) throw new Error('Invalid report ID')
      this.handleStatus(result)
    },
    async onConfirm () {
      if (this.busy || this.disposed || this.step !== 1) return
      if (!this.availabilityChecked || (this.reportId && !this.report)) return this.restoreReport()

      this.busy = true
      try {
        if (this.reportId) {
          await this.loadStatus()
          if (this.disposed || this.report.status !== 'error') return
        }
        const source = this.report || { sid: this.sid, domain: this.domain, lang: this.$i18n.locale }
        if (!source.sid || !source.domain) throw new Error('Missing report source')
        this.step = 2
        this.progressStep = 1
        const result = await createPeekaiReport({
          userNum: String(this.userNum),
          sid: source.sid,
          domain: source.domain,
          serviceType: 'beusable',
          lang: source.lang || this.$i18n.locale
        })
        if (this.disposed) return
        if (!result || !result.id) throw new Error('Missing report ID')
        this.reportId = result.id
        this.handleStatus(result)
      } catch (error) {
        if (this.disposed) return
        if (error.code === 409 && error.reportId) {
          this.reportId = error.reportId
          this.report = null
          await this.loadStatus().catch(() => this.showError())
        } else {
          this.availabilityChecked = false
          this.showError()
        }
      } finally {
        this.busy = false
      }
    },
    onViewReport () {
      if (this.busy || !this.report || !this.report.reportAvailable || !this.reportId) return
      const { href } = this.$router.resolve({
        name: 'peekai-report',
        params: { reportId: this.reportId }
      })
      window.open(href, '_blank')
    },
    startPolling () {
      this.stopPolling()
      this.pollingTimer = setTimeout(() => {
        this.loadStatus().catch(() => this.showError())
      }, 3000)
    },
    stopPolling () {
      if (this.pollingTimer !== null) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    handleStatus (result) {
      this.stopPolling()
      const wasError = this.report && this.report.status === 'error'
      this.report = result
      if (result.reportAvailable) {
        this.step = 3
        this.progressStep = 4
      } else if (result.status === 'error') {
        this.step = 1
        if (!wasError) openDialog({ dialogType: 'common.error' })
      } else {
        this.step = 2
        const currentStep = result.progress && result.progress.currentStep
        this.progressStep = Number.isInteger(currentStep) && currentStep >= 1 && currentStep <= 4 ? currentStep : 1
        this.startPolling()
      }
    },
    showError () {
      if (this.disposed) return
      this.stopPolling()
      this.step = 1
      openDialog({ dialogType: 'common.error' })
    }
  }
}
</script>
